import type { SelectionList, SelectionListIdNames } from '@revelio/iso-utility';
import { SelectionCategories } from '@revelio/iso-utility';

import { isRoleSelectionList } from '../../utils/role-taxonomy-utils';

const API_KEY_TO_FETCH_KEY: Record<string, SelectionListIdNames> = {
  role_k7: SelectionCategories.JOB_CATEGORY,
  role_k10: SelectionCategories.JOB_CATEGORY,
  metro_area: SelectionCategories.MSA,
};

const FETCH_KEY_TO_API_KEY: Record<string, string> = {
  [SelectionCategories.JOB_CATEGORY]: 'role_k10',
  [SelectionCategories.MSA]: 'metro_area',
};

const PARENT_FIELD_ALIASES: ReadonlyMap<string, string> = new Map([
  [SelectionCategories.MSA, SelectionCategories.METRO_AREA],
]);

export function addParentFieldAliases<T extends Record<string, unknown>>(
  items: T[]
): T[] {
  return items.map((item) => {
    const plain = item as Record<string, unknown>;
    const copy: Record<string, unknown> = Object.assign({}, plain);
    let changed = false;
    for (const [listId, fieldName] of PARENT_FIELD_ALIASES) {
      if (copy[fieldName] !== undefined && copy[listId] === undefined) {
        copy[listId] = copy[fieldName];
        changed = true;
      }
    }
    return (changed ? copy : plain) as T;
  });
}

export function toFetchKey(apiKey: string): SelectionListIdNames {
  return (
    API_KEY_TO_FETCH_KEY[apiKey] ?? (apiKey as unknown as SelectionListIdNames)
  );
}

/**
 * Resolve a human-readable label for a selection list item by looking it up
 * in the provided selection lists. Returns the item's existing label if
 * it is already resolved or the list isn't available.
 */
export function resolveSelectionListItemLabel(
  itemId: string,
  selectionListId: string,
  selectionLists: SelectionList[]
): string | null {
  const fetchKey = toFetchKey(selectionListId);
  const list = selectionLists.find((l) => String(l.id) === String(fetchKey));
  if (!list) return null;
  const found = list.value?.find((v) => String(v.id) === String(itemId));
  return found?.label ?? found?.longName ?? found?.shortName ?? null;
}

export function toApiKey(fetchKey: SelectionListIdNames): string {
  return FETCH_KEY_TO_API_KEY[fetchKey] ?? fetchKey;
}

export type SelectionListCategory = 'role' | 'skill' | 'gql';

const V3_SKILL_KEYS = new Set<string>([
  SelectionCategories.SKILL_K150,
  SelectionCategories.SKILL_K1500,
  SelectionCategories.SKILL_K5000,
  SelectionCategories.SKILL_K15000,
]);

const V2_SKILL_KEYS = new Set<string>([
  SelectionCategories.SKILL_K75,
  SelectionCategories.SKILL_K700,
  SelectionCategories.SKILL_K3000,
]);

export function isAnySkillKey(key: string): boolean {
  return (
    key.startsWith('skill_') || V3_SKILL_KEYS.has(key) || V2_SKILL_KEYS.has(key)
  );
}

export function classifyKey(fetchKey: string): SelectionListCategory {
  if (isRoleSelectionList(fetchKey)) return 'role';
  if (isAnySkillKey(fetchKey)) return 'skill';
  return 'gql';
}

const ROLE_HIERARCHY_V2: SelectionListIdNames[] = [
  SelectionCategories.JOB_CATEGORY,
  SelectionCategories.ROLE_K50,
  SelectionCategories.ROLE_K150,
  SelectionCategories.ROLE_K300,
  SelectionCategories.ROLE_K500,
  SelectionCategories.ROLE_K1000,
  SelectionCategories.ROLE_K1250,
  SelectionCategories.ROLE_K1500,
];

const ROLE_HIERARCHY_V3: SelectionListIdNames[] = [
  SelectionCategories.JOB_CATEGORY,
  SelectionCategories.ROLE_K50,
  SelectionCategories.ROLE_K150,
  SelectionCategories.ROLE_K500,
  SelectionCategories.ROLE_K1500,
  SelectionCategories.ROLE_K5000,
  SelectionCategories.ROLE_K15000,
];

const SKILL_HIERARCHY_V2: SelectionListIdNames[] = [
  SelectionCategories.SKILL_K75,
  SelectionCategories.SKILL_K700,
  SelectionCategories.SKILL_K3000,
];

const SKILL_HIERARCHY_V3: SelectionListIdNames[] = [
  SelectionCategories.SKILL_K150,
  SelectionCategories.SKILL_K1500,
  SelectionCategories.SKILL_K5000,
  SelectionCategories.SKILL_K15000,
];

export function getRoleHierarchy(isV3: boolean): SelectionListIdNames[] {
  return isV3 ? ROLE_HIERARCHY_V3 : ROLE_HIERARCHY_V2;
}

export function getSkillHierarchy(isV3: boolean): SelectionListIdNames[] {
  return isV3 ? SKILL_HIERARCHY_V3 : SKILL_HIERARCHY_V2;
}

export type ResolvedKeys = {
  all: SelectionListIdNames[];
  role: SelectionListIdNames[];
  skill: SelectionListIdNames[];
  gql: SelectionListIdNames[];
};

/**
 * Given backend API keys and taxonomy versions, replaces any role/skill keys
 * with the correct full hierarchy for that version. GQL keys pass through unchanged.
 * The position of the first role/skill key determines where the hierarchy is inserted.
 */
export function resolveHierarchyKeys(
  apiKeys: string[],
  isV3Job: boolean,
  isV3Skill: boolean
): ResolvedKeys {
  const fetchKeys = apiKeys.map(toFetchKey);

  const hasRole = fetchKeys.some((k) => classifyKey(k) === 'role');
  const hasSkill = fetchKeys.some((k) => classifyKey(k) === 'skill');

  const roleKeys = hasRole ? getRoleHierarchy(isV3Job) : [];
  const skillKeys = hasSkill ? getSkillHierarchy(isV3Skill) : [];
  const gqlKeys = fetchKeys.filter((k) => classifyKey(k) === 'gql');

  const all: SelectionListIdNames[] = [];
  let roleInserted = false;
  let skillInserted = false;

  for (const fk of fetchKeys) {
    const cat = classifyKey(fk);
    if (cat === 'role' && !roleInserted) {
      all.push(...roleKeys);
      roleInserted = true;
    } else if (cat === 'skill' && !skillInserted) {
      all.push(...skillKeys);
      skillInserted = true;
    } else if (cat === 'gql') {
      all.push(fk);
    }
  }

  return { all, role: roleKeys, skill: skillKeys, gql: gqlKeys };
}

/**
 * Validates that requested keys form consecutive entries in the valid hierarchy.
 * Returns a warning message if levels are skipped, null otherwise.
 */
export function validateHierarchy(
  requestedKeys: SelectionListIdNames[],
  validChain: SelectionListIdNames[]
): string | null {
  if (requestedKeys.length <= 1) return null;

  const indices = requestedKeys
    .map((k) => validChain.indexOf(k))
    .filter((i) => i !== -1);

  if (indices.length <= 1) return null;

  for (let i = 1; i < indices.length; i++) {
    const prev = indices[i - 1];
    const curr = indices[i];
    if (prev === undefined || curr === undefined) continue;
    if (curr !== prev + 1) {
      const skipped = validChain.slice(prev + 1, curr);
      const parentLevel = validChain[curr - 1];
      return (
        `Selection list hierarchy skip detected: ` +
        `[${requestedKeys.join(', ')}] skips [${skipped.join(', ')}]. ` +
        `Items at level ${String(requestedKeys[i])} reference ${String(parentLevel)} as parent, ` +
        `which is not in the requested hierarchy. ` +
        `The tree will show items ungrouped at that level.`
      );
    }
  }

  return null;
}
